import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, onSnapshot, collection, addDoc, query, where, orderBy, serverTimestamp, getDoc } from "firebase/firestore";
import { db } from "../../../Firebase";
import { V3Room, V3Event, V3Problem, V3Submission } from "../types/v3types";
import { useAuth } from "../hooks/useAuth";
import styled, { createGlobalStyle } from "styled-components";
import { getCustomComponent } from "../components/customComponents";

const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    overflow-x: hidden;
  }
  
  * {
    box-sizing: border-box;
  }
`;

const Container = styled.div`
  min-height: 100vh;
  background: #1a1a1a;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-x: hidden;
  background-image: linear-gradient(45deg, #000 25%, #1a1a1a 25%, #1a1a1a 50%, #000 50%, #000 75%, #1a1a1a 75%, #1a1a1a);
  background-size: 40px 40px;
`;

const Header = styled.header`
  background: linear-gradient(180deg, #dc143c 0%, #8b0000 100%);
  color: white;
  padding: 15px 20px;
  box-shadow: 0 4px 15px rgba(220, 20, 60, 0.3);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  border-bottom: 3px solid #ff0000;
  
  @media (max-width: 600px) {
    padding: 10px 15px;
  }
`;

const Title = styled.h1`
  font-size: 20px;
  margin: 0;
  
  @media (max-width: 600px) {
    font-size: 18px;
  }
`;

const StatusInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  
  @media (max-width: 600px) {
    gap: 10px;
  }
`;

const TimerContainer = styled.div`
  width: 200px;
  height: 30px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 15px;
  padding: 3px;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 600px) {
    width: 150px;
    height: 25px;
  }
`;

const TimerBar = styled.div<{ progress: number }>`
  height: 100%;
  background: linear-gradient(90deg, #ff4444 0%, #ff6666 100%);
  border-radius: 13px;
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
  box-shadow: 0 0 10px rgba(255, 68, 68, 0.5);
`;

const TimerText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 14px;
  font-weight: bold;
  color: white;
  text-shadow: 0 0 3px rgba(0, 0, 0, 0.8);
`;

const Score = styled.div`
  font-size: 18px;
  font-weight: 500;
  background: rgba(0, 0, 0, 0.3);
  padding: 5px 15px;
  border-radius: 20px;
  
  @media (max-width: 600px) {
    font-size: 16px;
    padding: 5px 10px;
  }
`;

const Content = styled.main`
  flex: 1;
  padding: 20px;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  
  @media (max-width: 600px) {
    padding: 15px;
  }
`;

const WaitingScreen = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  border: 3px solid #dc143c;
  box-shadow: 0 0 20px rgba(220, 20, 60, 0.3);
  backdrop-filter: blur(5px);
`;

const WaitingTitle = styled.h2`
  font-size: 36px;
  margin-bottom: 20px;
  color: #dc143c;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const WaitingMessage = styled.p`
  font-size: 20px;
  color: #666;
  margin-bottom: 40px;
`;

const ParticipantCount = styled.div`
  font-size: 24px;
  color: #dc143c;
  font-weight: 600;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
`;

const ProblemContainer = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border: 3px solid #dc143c;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 0 20px rgba(220, 20, 60, 0.3);
  backdrop-filter: blur(5px);
  
  @media (max-width: 600px) {
    padding: 20px;
    border-radius: 0;
    border-width: 2px;
  }
`;

const ProblemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 10px;
  
  @media (max-width: 600px) {
    margin-bottom: 20px;
  }
`;

const ProblemNumber = styled.h2`
  font-size: 24px;
  margin: 0;
  color: #dc143c;
  font-weight: bold;
  
  @media (max-width: 600px) {
    font-size: 20px;
  }
`;

const SubmissionCount = styled.div`
  font-size: 16px;
  color: #666;
  
  @media (max-width: 600px) {
    font-size: 14px;
  }
`;

const ProblemImageContainer = styled.div`
  text-align: center;
  margin-bottom: 30px;
  
  @media (max-width: 600px) {
    margin-bottom: 20px;
  }
`;

const ProblemImage = styled.img`
  max-width: 100%;
  max-height: 400px;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 0 15px rgba(220, 20, 60, 0.3);
  border: 2px solid #dc143c;
  transition: transform 0.2s;
  
  &:hover {
    transform: scale(1.02);
  }
  
  @media (max-width: 600px) {
    max-height: 300px;
    border-radius: 4px;
  }
`;

const AnswerForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const AnswerInput = styled.input`
  padding: 15px 20px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 18px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #dc143c;
    box-shadow: 0 0 0 3px rgba(220, 20, 60, 0.1);
  }

  &:disabled {
    background: #f5f5f5;
  }
`;

const ChoiceContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const ChoiceButton = styled.button<{ selected: boolean }>`
  padding: 15px 20px;
  border: 2px solid ${props => props.selected ? '#dc143c' : '#e0e0e0'};
  background: ${props => props.selected ? '#dc143c' : 'white'};
  color: ${props => props.selected ? 'white' : '#333'};
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(220, 20, 60, 0.2);
    border-color: #dc143c;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const SubmitButton = styled.button`
  padding: 15px 30px;
  background: linear-gradient(135deg, #dc143c 0%, #8b0000 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  align-self: center;
  min-width: 200px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(220, 20, 60, 0.3);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const ResultContainer = styled.div`
  margin-top: 20px;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
`;

const CorrectResult = styled(ResultContainer)`
  background: #d4edda;
  color: #155724;
  border: 2px solid #28a745;
  box-shadow: 0 0 15px rgba(40, 167, 69, 0.3);
`;

const IncorrectResult = styled(ResultContainer)`
  background: #f8d7da;
  color: #721c24;
  border: 2px solid #dc3545;
  box-shadow: 0 0 15px rgba(220, 53, 69, 0.3);
`;

const ResultText = styled.div`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 10px;
`;

const PointsEarned = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #28a745;
`;

const RankingContainer = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border: 3px solid #dc143c;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 0 20px rgba(220, 20, 60, 0.3);
  backdrop-filter: blur(5px);
`;

const RankingTitle = styled.h2`
  font-size: 28px;
  text-align: center;
  margin-bottom: 30px;
  color: #dc143c;
  font-weight: bold;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
`;

const RankingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const RankingItem = styled.div<{ isMe: boolean }>`
  display: flex;
  align-items: center;
  padding: 15px 20px;
  background: ${props => props.isMe ? 'rgba(220, 20, 60, 0.1)' : '#f8f9fa'};
  border: ${props => props.isMe ? '2px solid #dc143c' : '1px solid #e0e0e0'};
  border-radius: 8px;
  box-shadow: ${props => props.isMe ? '0 0 10px rgba(220, 20, 60, 0.2)' : 'none'};
`;

const RankNumber = styled.div`
  font-size: 24px;
  font-weight: bold;
  margin-right: 20px;
  min-width: 40px;
  text-align: center;
  color: #666;
`;

const RankName = styled.div<{ isMe?: boolean }>`
  flex: 1;
  font-size: 18px;
  font-weight: ${props => props.isMe ? 'bold' : 'normal'};
`;

const RankScore = styled.div`
  font-size: 20px;
  font-weight: bold;
  color: #333;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px;
  font-size: 18px;
  color: #666;
`;

const V3Game: React.FC = () => {
  const { projectId, roomId } = useParams<{ projectId: string; roomId: string }>();
  const navigate = useNavigate();
  const { user: firebaseUser, loading: authLoading } = useAuth();
  const [room, setRoom] = useState<V3Room | null>(null);
  const [event, setEvent] = useState<V3Event | null>(null);
  const [currentProblem, setCurrentProblem] = useState<V3Problem | null>(null);
  const [submissions, setSubmissions] = useState<V3Submission[]>([]);
  const [mySubmissions, setMySubmissions] = useState<V3Submission[]>([]);
  const [answer, setAnswer] = useState("");
  const [selectedChoices, setSelectedChoices] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<{ correct: boolean; points: number } | null>(null);
  const [timer, setTimer] = useState<number>(0);
  const [totalScore, setTotalScore] = useState(0);
  const [userId, setUserId] = useState<string>("");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showBeforeImages, setShowBeforeImages] = useState(true);

  useEffect(() => {
    if (!projectId || !roomId) {
      navigate("/");
      return;
    }

    if (authLoading) return;

    if (!firebaseUser) {
      navigate(`/v3/${projectId}/start`);
      return;
    }

    const sessionUserId = firebaseUser.uid + "_" + projectId;
    setUserId(sessionUserId);

    // Track current problem ID to detect changes
    let previousProblemId: string | null = null;

    // Subscribe to room updates
    const unsubscribeRoom = onSnapshot(doc(db, "v3rooms", roomId), async (docSnapshot) => {
      if (docSnapshot.exists()) {
        const roomData = docSnapshot.data() as V3Room;
        setRoom(roomData);

        // Load event data
        if (roomData.eventId) {
          const eventDocRef = doc(db, "v3events", roomData.eventId);
          const eventDoc = await getDoc(eventDocRef);
          if (eventDoc.exists()) {
            const eventData = eventDoc.data() as V3Event;
            setEvent(eventData);
            
            if (eventData.problems && eventData.problems[roomData.currentProblemIndex]) {
              const problem = eventData.problems[roomData.currentProblemIndex];
              
              // Only reset form fields if the problem has changed
              if (problem.problemId !== previousProblemId) {
                previousProblemId = problem.problemId;
                setCurrentProblem(problem);
                setAnswer("");
                setSelectedChoices([]);
                setLastResult(null);
                setCurrentImageIndex(0);
                setShowBeforeImages(!!problem.beforeImageUrls && problem.beforeImageUrls.length > 0);
              } else {
                // Just update the problem without resetting form fields
                setCurrentProblem(problem);
              }
              
              // Always update timer based on problem status and server time
              if (roomData.problemStatuses?.[problem.problemId] === "timing") {
                if (roomData.problemStartTimes?.[problem.problemId]) {
                  // Calculate remaining time based on server start time
                  const startTime = roomData.problemStartTimes[problem.problemId].toMillis();
                  const currentTime = Date.now();
                  const elapsed = Math.round((currentTime - startTime) / 1000);
                  const remaining = Math.max(0, problem.timeLimit - elapsed);
                  setTimer(remaining);
                } else if (roomData.problemTimers?.[problem.problemId]) {
                  setTimer(roomData.problemTimers[problem.problemId]);
                } else {
                  setTimer(problem.timeLimit);
                }
              } else {
                setTimer(problem.timeLimit);
              }
            }
          }
        }
      }
    });

    // Subscribe to all submissions
    const submissionsQuery = query(
      collection(db, "v3submissions"),
      where("roomId", "==", roomId)
    );
    
    const unsubscribeSubmissions = onSnapshot(submissionsQuery, (snapshot) => {
      const allSubs = snapshot.docs.map(doc => doc.data() as V3Submission);
      setSubmissions(allSubs);
      
      const mySubs = allSubs.filter(sub => sub.userId === sessionUserId);
      setMySubmissions(mySubs);
      
      const myScore = mySubs
        .filter(sub => sub.isCorrect)
        .reduce((total, sub) => total + sub.score, 0);
      setTotalScore(myScore);
    });

    return () => {
      unsubscribeRoom();
      unsubscribeSubmissions();
    };
  }, [projectId, roomId, navigate, firebaseUser, authLoading]);

  useEffect(() => {
    // Only countdown when status is "timing"
    if (room && currentProblem && room.problemStatuses?.[currentProblem.problemId] === "timing" && timer > 0) {
      const interval = setInterval(() => {
        setTimer(prev => {
          // Double check status is still "timing" before decrementing
          if (room.problemStatuses?.[currentProblem.problemId] !== "timing" || prev <= 0) {
            clearInterval(interval);
            return prev;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [room, currentProblem, room?.problemStatuses, timer]);

  // Auto image switching
  useEffect(() => {
    if (!currentProblem || !currentProblem.imageSettings || currentProblem.imageSettings.switchMode !== "auto") {
      return;
    }

    const interval = setInterval(() => {
      if (showBeforeImages && currentProblem.beforeImageUrls && currentProblem.beforeImageUrls.length > 0) {
        if (currentImageIndex < currentProblem.beforeImageUrls.length - 1) {
          setCurrentImageIndex(prev => prev + 1);
        } else {
          setShowBeforeImages(false);
          setCurrentImageIndex(0);
        }
      } else if (currentProblem.imageUrls.length > 1) {
        setCurrentImageIndex(prev => (prev + 1) % currentProblem.imageUrls.length);
      }
    }, (currentProblem.imageSettings.autoSwitchInterval || 5) * 1000);

    return () => clearInterval(interval);
  }, [currentProblem, currentImageIndex, showBeforeImages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentProblem || !room || !userId || submitting) return;

    // Check if already submitted max times
    const problemSubmissions = mySubmissions.filter(
      sub => sub.problemId === currentProblem.problemId
    );
    
    if (problemSubmissions.length >= currentProblem.maxSubmissions) {
      return;
    }

    setSubmitting(true);

    try {
      const submissionAnswer = currentProblem.inputType === "choice" ? selectedChoices[0] :
                             currentProblem.inputType === "multiple" ? selectedChoices.join(",") :
                             answer;

      const isCorrect = currentProblem.answers.some(correctAnswer => 
        correctAnswer.toLowerCase() === submissionAnswer.toLowerCase()
      );

      // Calculate actual time taken based on problem start time with millisecond precision
      let timeTaken = currentProblem.timeLimit - timer;
      let remainingTime = timer; // Default to local timer
      
      // If we have a server-side start time, calculate more accurately
      if (room.problemStartTimes?.[currentProblem.problemId]) {
        const startTime = room.problemStartTimes[currentProblem.problemId].toMillis();
        const currentTime = Date.now();
        // Calculate with millisecond precision (keep 3 decimal places)
        const elapsedMs = currentTime - startTime;
        timeTaken = Math.round(elapsedMs) / 1000; // This preserves exact millisecond precision
        remainingTime = Math.max(0, currentProblem.timeLimit - timeTaken);
      }
      
      let score = 0;
      
      if (isCorrect) {
        // Check for custom scoring
        if (event?.scoringMode === "custom" && event?.eventName === "缶詰セット") {
          // 缶詰セット特別ルール: 100 + 2 × 残り時間（秒）
          score = 100 + (2 * remainingTime);
        } else if (event?.scoringMode === "dynamic") {
          // Dynamic scoring: base score (100-200 based on time) × 1.2^(problem index)
          const timeRatio = remainingTime / currentProblem.timeLimit;
          const baseScore = 100 + (100 * timeRatio); // 100点〜200点
          const multiplier = Math.pow(1.2, room.currentProblemIndex);
          score = Math.round(baseScore * multiplier);
        } else if (event?.scoringMode === "normal") {
          // Normal scoring: 100 + 100 × (remaining time / time limit)
          const timeRatio = remainingTime / currentProblem.timeLimit;
          score = Math.round(100 + (100 * timeRatio));
        } else {
          // Default scoring (same as normal)
          const timeRatio = remainingTime / currentProblem.timeLimit;
          score = Math.round(100 + (100 * timeRatio));
        }
      }

      const submission: Omit<V3Submission, "submissionId" | "submittedAt"> = {
        roomId: roomId!,
        userId,
        problemId: currentProblem.problemId,
        answer: submissionAnswer,
        isCorrect,
        score,
        submissionNumber: problemSubmissions.length + 1,
        timeTaken,
      };

      await addDoc(collection(db, "v3submissions"), {
        ...submission,
        submissionId: `${userId}_${currentProblem.problemId}_${Date.now()}`,
        submittedAt: serverTimestamp(),
      });

      setLastResult({ correct: isCorrect, points: score });
    } catch (error) {
      console.error("Error submitting answer:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleChoice = (choice: string) => {
    if (currentProblem?.inputType === "choice") {
      setSelectedChoices([choice]);
    } else if (currentProblem?.inputType === "multiple") {
      setSelectedChoices(prev => 
        prev.includes(choice) 
          ? prev.filter(c => c !== choice)
          : [...prev, choice]
      );
    }
  };

  if (!room || !event || authLoading) {
    return (
      <Container>
        <Content>
          <LoadingState>読み込み中...</LoadingState>
        </Content>
      </Container>
    );
  }

  const problemSubmissions = currentProblem ? 
    mySubmissions.filter(sub => sub.problemId === currentProblem.problemId) : [];
  const canSubmit = currentProblem && problemSubmissions.length < currentProblem.maxSubmissions;

  return (
    <>
      <GlobalStyle />
      <Container>
        <Header>
          <Title>{event.eventName} - {room.roomName}</Title>
          <StatusInfo>
            {currentProblem && room.problemStatuses?.[currentProblem.problemId] === "timing" && (
              <TimerContainer>
                <TimerBar progress={(timer / currentProblem.timeLimit) * 100} />
                <TimerText>{formatTime(timer)}</TimerText>
              </TimerContainer>
            )}
            <Score>スコア: {totalScore}点</Score>
          </StatusInfo>
        </Header>

        <Content>
        {room.displayMode === "waiting" && (
          <WaitingScreen>
            <WaitingTitle>開始を待っています</WaitingTitle>
            <WaitingMessage>まもなくゲームが始まります</WaitingMessage>
            <ParticipantCount>
              現在の参加者: {room.participants?.length || 0}人
            </ParticipantCount>
          </WaitingScreen>
        )}

        {(room.displayMode === "problem" || room.displayMode === "problemWithTimer") && currentProblem && (
          <ProblemContainer>
            <ProblemHeader>
              <div>
                <ProblemNumber>
                  問題 {room.currentProblemIndex + 1} / {event.problems.length}
                </ProblemNumber>
                {event.scoringMode && event.scoringMode !== "custom" && (
                  <div style={{ fontSize: '14px', color: '#999', marginTop: '4px' }}>
                    {event.scoringMode === "dynamic" 
                      ? `基本点 ${currentProblem.baseScore}点` 
                      : `配点 ${currentProblem.baseScore}点`}
                  </div>
                )}
              </div>
              <SubmissionCount>
                解答回数: {problemSubmissions.length} / {currentProblem.maxSubmissions}
              </SubmissionCount>
            </ProblemHeader>

            {/* Check problem status - show appropriate message based on status */}
            {room.problemStatuses?.[currentProblem.problemId] === "waiting" ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
                <h2 style={{ fontSize: '24px', marginBottom: '20px' }}>準備中...</h2>
                <p>まもなく問題が表示されます</p>
              </div>
            ) : room.problemStatuses?.[currentProblem.problemId] === "finished" ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
                <h2 style={{ fontSize: '24px', marginBottom: '20px' }}>時間切れ</h2>
                <p>この問題は終了しました</p>
              </div>
            ) : (
              <>

            {/* Check for custom component first */}
            {currentProblem.customComponent ? (
              <ProblemImageContainer>
                {(() => {
                  const CustomComponent = getCustomComponent(currentProblem.customComponent.type);
                  if (CustomComponent) {
                    return (
                      <CustomComponent
                        problemId={currentProblem.problemId}
                        currentImageIndex={currentImageIndex}
                        imageUrls={currentProblem.imageUrls}
                        beforeImageUrls={currentProblem.beforeImageUrls}
                        onImageChange={setCurrentImageIndex}
                        customProps={currentProblem.customComponent.props}
                      />
                    );
                  }
                  return <div>カスタムコンポーネントが見つかりません</div>;
                })()}
              </ProblemImageContainer>
            ) : (
              /* Default image display */
              (showBeforeImages && currentProblem.beforeImageUrls && currentProblem.beforeImageUrls.length > 0) ? (
                <ProblemImageContainer>
                  <ProblemImage 
                    src={currentProblem.beforeImageUrls[Math.min(currentImageIndex, currentProblem.beforeImageUrls.length - 1)]} 
                    alt="Before" 
                    onClick={() => {
                      if (currentProblem.imageSettings?.switchMode === "manual" || !currentProblem.imageSettings) {
                        if (currentImageIndex < currentProblem.beforeImageUrls!.length - 1) {
                          setCurrentImageIndex(prev => prev + 1);
                        } else {
                          setShowBeforeImages(false);
                          setCurrentImageIndex(0);
                        }
                      }
                    }}
                    style={{ cursor: currentProblem.imageSettings?.switchMode === "manual" || !currentProblem.imageSettings ? "pointer" : "default" }}
                  />
                  {(currentProblem.imageSettings?.switchMode === "manual" || !currentProblem.imageSettings) && (
                    <div style={{ textAlign: "center", marginTop: "10px", color: "#666" }}>
                      クリックして次へ ({currentImageIndex + 1}/{currentProblem.beforeImageUrls.length + currentProblem.imageUrls.length})
                    </div>
                  )}
                </ProblemImageContainer>
              ) : currentProblem.imageUrls.length > 0 && (
                <ProblemImageContainer>
                  <ProblemImage 
                    src={currentProblem.imageUrls[Math.min(currentImageIndex, currentProblem.imageUrls.length - 1)]} 
                    alt="Problem" 
                    onClick={() => {
                      if ((currentProblem.imageSettings?.switchMode === "manual" || !currentProblem.imageSettings) && currentProblem.imageUrls.length > 1) {
                        setCurrentImageIndex(prev => (prev + 1) % currentProblem.imageUrls.length);
                      }
                    }}
                    style={{ cursor: (currentProblem.imageSettings?.switchMode === "manual" || !currentProblem.imageSettings) && currentProblem.imageUrls.length > 1 ? "pointer" : "default" }}
                  />
                  {(currentProblem.imageSettings?.switchMode === "manual" || !currentProblem.imageSettings) && currentProblem.imageUrls.length > 1 && (
                    <div style={{ textAlign: "center", marginTop: "10px", color: "#666" }}>
                      クリックして切り替え ({currentImageIndex + 1}/{currentProblem.imageUrls.length})
                    </div>
                  )}
                </ProblemImageContainer>
              )
            )}

            <AnswerForm onSubmit={handleSubmit}>
              {currentProblem.inputType === "text" || currentProblem.inputType === "number" ? (
                <AnswerInput
                  type={currentProblem.inputType}
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="答えを入力してください"
                  disabled={!canSubmit || submitting || room.problemStatuses?.[currentProblem.problemId] === "finished"}
                  autoComplete="off"
                />
              ) : currentProblem.choices && (
                <ChoiceContainer>
                  {currentProblem.choices.map((choice, index) => (
                    <ChoiceButton
                      key={index}
                      type="button"
                      selected={selectedChoices.includes(choice)}
                      onClick={() => toggleChoice(choice)}
                      disabled={!canSubmit || submitting || room.problemStatuses?.[currentProblem.problemId] === "finished"}
                    >
                      {choice}
                    </ChoiceButton>
                  ))}
                </ChoiceContainer>
              )}

              <SubmitButton 
                type="submit" 
                disabled={!canSubmit || submitting || 
                  room.problemStatuses?.[currentProblem.problemId] === "finished" ||
                  (currentProblem.inputType === "text" && !answer) ||
                  ((currentProblem.inputType === "choice" || currentProblem.inputType === "multiple") && selectedChoices.length === 0)
                }
              >
                {submitting ? "送信中..." : "解答する"}
              </SubmitButton>
            </AnswerForm>

            {lastResult && (
              lastResult.correct ? (
                <CorrectResult>
                  <ResultText>正解！</ResultText>
                  <PointsEarned>+{lastResult.points}点</PointsEarned>
                </CorrectResult>
              ) : (
                <IncorrectResult>
                  <ResultText>不正解</ResultText>
                  {canSubmit && <div>もう一度チャレンジしてください</div>}
                </IncorrectResult>
              )
            )}
            </>
            )}
          </ProblemContainer>
        )}

        {room.displayMode === "answer" && currentProblem && (
          <ProblemContainer>
            <ProblemHeader>
              <ProblemNumber>問題 {room.currentProblemIndex + 1} - 解答</ProblemNumber>
            </ProblemHeader>
            
            <ResultContainer style={{ background: '#f8f9fa', marginBottom: '20px' }}>
              <ResultText style={{ color: '#28a745' }}>
                正解: {currentProblem.answers.join(" または ")}
              </ResultText>
            </ResultContainer>

            {currentProblem.explanation && (
              <div style={{ fontSize: '18px', lineHeight: '1.8', color: '#555' }}>
                <h3>解説</h3>
                <p>{currentProblem.explanation}</p>
              </div>
            )}

            {currentProblem.answerImageUrls?.[0] && (
              <ProblemImageContainer>
                <ProblemImage src={currentProblem.answerImageUrls[0]} alt="Answer" />
              </ProblemImageContainer>
            )}
          </ProblemContainer>
        )}

        {(room.displayMode === "ranking" || room.displayMode === "finalRanking") && (
          <RankingContainer>
            <RankingTitle>
              {room.displayMode === "finalRanking" ? "最終結果" : "現在のランキング"}
            </RankingTitle>
            <RankingList>
              {/* ランキング表示のロジックはスクリーンと同様 */}
            </RankingList>
          </RankingContainer>
        )}
        </Content>
      </Container>
    </>
  );
};

export default V3Game;